// The scrivenings editor (Web/editor) hosted in the page: one CodeMirror view over the selection's fragments.
//
// It lives in a shadow root, so its styles are constructed style sheets — CodeMirror's own (style-mod adopts them)
// and the editor's style.css, which the build inlines as text (`__EDITOR_CSS__`) — which the app's `style-src 'self'`
// allows; `<style>` elements, which CodeMirror would add to the document, it refuses. The app's tokens (`--paper`,
// `--ink`, the inks) are custom properties and cross into the shadow root; the fonts' @font-face rules stay in the
// document's app.css, where they have to be. ⌘F's find bar (@codemirror/search) is a CodeMirror panel, so it lives in
// the shadow root too, styled here.
//
// Writing: each edit the editor reports goes to the fragment's Automerge text with `spliceText`, in order, in one
// `live.edit` (the outbox batches them). Another device's change arrives as Automerge patches and is applied with
// `applyExternal` (no undo entry, the caret kept); so is this device's own change made outside the editor (Replace
// All, a suggestion accepted from a list), in the same turn, so the next keystroke lands where it should. Each shown
// fragment remembers the document its text was last synced with: a rebuilt project touches only fragments whose
// document changed some other way (a safety net), and the whole selection only when titles, openers or the fragments
// themselves change.
//
// Comments and suggestions are the editor's annotations (fragment, UTF-16 range), resolved by the page.
import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import type * as A from '@automerge/automerge';
import type { Extension } from '@codemirror/state';
import { keymap } from '@codemirror/view';
import { search, searchKeymap } from '@codemirror/search';
import type { EditorEdit, EditorFragment, OpenedProject, Opener, Project } from '@writing-suite/core';
import {
  annotationRange, annotations as annotationExtension, createScrivenings, Exact, minimalChange, setActiveAnnotation, setAnnotations,
  type Annotation, type ProseFormat, type ScriveningsHandle,
} from '@writing-suite/editor';
import { registerOpenEditor } from '../store/reviseEditor.ts';
import { remoteSplices } from '../store/remote.ts';
import { useShowsFormattingMarks } from '../shell/preferences.ts';
import type { LiveProject } from '../store/project.ts';

declare const __EDITOR_CSS__: string;

/** Inside the shadow root: the host fills its box; composition mode's page on its backdrop; the find bar. */
const HOST_CSS = `
:host { display: block; position: relative; min-height: 0; }
.ws-host { height: 100%; }
.cm-editor.ws-scrivenings.ws-theme-book { --ws-line-height: var(--write-line-height, 1.68); background: transparent; }
.cm-editor.ws-scrivenings .cm-scroller { overscroll-behavior: contain; }
.cm-editor.ws-scrivenings.ws-theme-composition { background: var(--compose-backdrop, #16181d); }
.cm-editor.ws-scrivenings.ws-theme-composition .cm-content {
  background: var(--ws-paper);
  max-width: calc(var(--ws-measure) + 8em);
  padding: 4em 4em 45vh;
  min-height: 100%;
  box-shadow: 0 0 60px rgb(0 0 0 / 0.35);
}
.cm-editor.ws-scrivenings .cm-panels { background: var(--paper-raised, var(--ws-paper)); color: var(--ws-text); border-color: var(--rule, #ddd5c8); font-family: var(--ws-sans); }
.cm-editor.ws-scrivenings .cm-panels-top { border-bottom: 1px solid var(--rule, #ddd5c8); }
.cm-editor.ws-scrivenings .cm-search { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px; padding: 8px 12px; font-size: 13px; }
.cm-editor.ws-scrivenings .cm-search br { display: none; }
.cm-editor.ws-scrivenings .cm-search .cm-textfield {
  font: inherit; font-size: 14px; color: var(--ws-text); background: var(--paper, #fff); border: 1px solid var(--rule-strong, #cfc5b6);
  border-radius: 6px; padding: 4px 8px; min-width: 10em; margin: 0;
}
.cm-editor.ws-scrivenings .cm-search .cm-textfield:focus { outline: 2px solid var(--ws-accent); outline-offset: 1px; }
.cm-editor.ws-scrivenings .cm-search .cm-button {
  font: inherit; font-size: 13px; color: var(--ws-text); background: transparent; background-image: none;
  border: 1px solid var(--rule-strong, #cfc5b6); border-radius: 6px; padding: 3px 9px; margin: 0; cursor: pointer;
}
.cm-editor.ws-scrivenings .cm-search .cm-button:hover { border-color: var(--ws-accent); color: var(--ws-accent); }
.cm-editor.ws-scrivenings .cm-search label { display: inline-flex; align-items: center; gap: 4px; color: var(--ws-secondary); font-size: 13px; }
.cm-editor.ws-scrivenings .cm-search button[name="close"] { color: var(--ws-secondary); background: none; border: 0; font-size: 18px; cursor: pointer; margin-left: auto; }
.cm-editor.ws-scrivenings .cm-searchMatch { background: color-mix(in srgb, var(--ws-accent) 22%, transparent); border-radius: 2px; }
.cm-editor.ws-scrivenings .cm-searchMatch.cm-searchMatch-selected { background: color-mix(in srgb, var(--ws-accent) 45%, transparent); }
@media (max-width: 700px) {
  .cm-editor.ws-scrivenings.ws-theme-composition .cm-content { padding: 2em 1.2em 45vh; }
  .cm-editor.ws-scrivenings .cm-content { padding-left: 0.25em; padding-right: 0.25em; }
  .cm-editor.ws-scrivenings .cm-search .cm-textfield { min-width: 0; flex: 1 1 8em; }
}
`;

let sheet: CSSStyleSheet | null = null;
function editorSheet(): CSSStyleSheet {
  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheet.replaceSync((typeof __EDITOR_CSS__ === 'string' ? __EDITOR_CSS__ : '') + HOST_CSS);
  }
  return sheet;
}

/** The editor's fragments for these ids, their text as stored now (never an older rebuild's): the project's where
 *  it was built from the document as it is now, else read afresh (typed or changed since the rebuild). */
export function editorFragments(project: Project, live: LiveProject | null, ids: string[], openers: Map<string, Opener>): EditorFragment[] {
  const built = (project as Partial<OpenedProject>).fragmentDoc?.bind(project);
  return ids.flatMap((id) => {
    const f = project.fragment(id);
    if (!f) return [];
    const current = !live || (built && built(id) === live.doc(`fragment.${id}`));
    return [{ id, title: f.title, kind: f.kind, text: current ? f.text : live.text(id), opener: openers.get(id) ?? null }];
  });
}

/** What the selection's dividers show: the fragments, their titles, kinds and openers (a change redraws them all). */
export function piecesKey(project: Project, ids: string[], openers: Map<string, Opener>): string {
  return ids
    .map((id) => {
      const f = project.fragment(id);
      const o = openers.get(id);
      return f ? `${id}\u0001${f.title}\u0001${f.kind}\u0001${o ? `${o.kind}\u0002${o.number}\u0002${o.label}\u0002${o.title}` : ''}` : `${id}\u0001-`;
    })
    .join('\u0000');
}

/**
 * More CodeMirror extensions for Write's editor (a room's own: @ completion, a context menu's items…), each made for
 * the live project the editor opens on. Register at module load, before Write first shows: the editor reads them when
 * it's made (once per project). Returns a function that unregisters.
 */
export type EditorExtensionProvider = (o: { project: Project; live: LiveProject | null }) => Extension;
const editorExtensionProviders = new Set<EditorExtensionProvider>();
export function registerEditorExtension(provider: EditorExtensionProvider): () => void {
  editorExtensionProviders.add(provider);
  return () => editorExtensionProviders.delete(provider);
}

export interface ScriveningsApi {
  handle(): ScriveningsHandle | null;
  /** Selects a passage (UTF-16 in its fragment) as the app places it, scrolled into view, and focuses the editor (unless
   *  `focus` is false: a new post's caret, placed while its title is renamed). */
  reveal(fragmentId: string, from: number, to: number, focus?: boolean): boolean;
  /** An annotation's range as the editor has it now (mapped through typing since it was resolved). */
  annotationRange(id: string): { fragmentId: string; from: number; to: number } | null;
  /** Replaces a passage through the editor (so it can be undone), as the writer's own edit. */
  replace(fragmentId: string, from: number, to: number, insert: string): boolean;
  /** Replaces a passage exactly as given (never balanced for hidden syntax: Reject's base text), undoably. */
  replaceExact(fragmentId: string, from: number, to: number, insert: string): boolean;
  /** A shown fragment's text as the editor has it (null when it isn't shown). */
  text(fragmentId: string): string | null;
}

interface Props {
  project: Project;
  live: LiveProject | null;
  ids: string[];
  openers: Map<string, Opener>;
  readOnly: boolean;
  /** Fragments under lock (`effectiveLocks`): the editor refuses typing in them. */
  locked?: readonly string[];
  composition: boolean;
  /** Where the caret starts when the selection changes (Left off), if it's in it. */
  startAt: { fragmentId: string; offset: number } | null;
  label: string;
  /** Comments and suggestions to draw, and the one chosen in the margin. */
  annotations: Annotation[];
  activeAnnotation: string | null;
  onCaret(caret: { fragmentId: string; offset: number } | null): void;
  onFormat(format: ProseFormat | null): void;
  /** The words in the selection as it changes; null once nothing (or no words) is selected. */
  onSelection?(words: number | null): void;
  onTyped(): void;
  /** The annotations the caret is in, as it moves. */
  onAnnotationCaret(ids: string[]): void;
  /** A right-click in the text (the page decides whether to show its own menu). */
  onContextMenu(event: React.MouseEvent): void;
  /** ⌘-click (Ctrl-click elsewhere) on a link's words: opens its address. */
  onOpenLink?(address: string): void;
  apiRef: Ref<ScriveningsApi>;
}

export function Scrivenings(props: Props) {
  const { project, live, ids, openers, readOnly, composition, startAt, label, apiRef } = props;
  const host = useRef<HTMLDivElement>(null);
  const handle = useRef<ScriveningsHandle | null>(null);
  /** Each shown fragment's text as the editor has it (its edits and the remote changes applied). */
  const mirror = useRef(new Map<string, string>());
  /** The document each shown fragment's text was last synced with. */
  const synced = useRef(new Map<string, A.Doc<unknown> | undefined>());
  const callbacks = useRef(props);
  callbacks.current = props;
  const shownKey = useRef('');
  const piecesShown = useRef('');
  const showsMarks = useShowsFormattingMarks();
  const showsMarksNow = useRef(showsMarks);
  showsMarksNow.current = showsMarks;

  useImperativeHandle(
    apiRef,
    () => ({
      handle: () => handle.current,
      reveal(fragmentId, from, to, focus = true) {
        const editor = handle.current;
        if (!editor || !mirror.current.has(fragmentId)) return false;
        editor.select(fragmentId, from, to, { exact: true, scroll: true });
        if (focus) editor.focus();
        return true;
      },
      annotationRange: (id) => (handle.current ? annotationRange(handle.current.view.state, id) : null),
      replace(fragmentId, from, to, insert) {
        const editor = handle.current;
        if (!editor || !mirror.current.has(fragmentId)) return false;
        editor.select(fragmentId, from, to, { exact: true, scroll: false });
        const r = editor.view.state.selection.main;
        editor.view.dispatch({ changes: { from: r.from, to: r.to, insert }, selection: { anchor: r.from + insert.length }, userEvent: 'input.replace' });
        editor.focus();
        return true;
      },
      replaceExact(fragmentId, from, to, insert) {
        const editor = handle.current;
        if (!editor || !mirror.current.has(fragmentId) || editor.view.state.readOnly) return false;
        editor.select(fragmentId, from, to, { exact: true, scroll: false });
        const r = editor.view.state.selection.main;
        editor.view.dispatch({ changes: { from: r.from, to: r.to, insert }, selection: { anchor: r.from + insert.length }, annotations: Exact.of(true), userEvent: 'input.replace', scrollIntoView: true });
        editor.focus();
        return true;
      },
      text: (fragmentId) => mirror.current.get(fragmentId) ?? null,
    }),
    [],
  );

  const syncAll = (editor: ScriveningsHandle, fragments: EditorFragment[]) => {
    mirror.current = new Map(fragments.map((f) => [f.id, f.text]));
    synced.current = new Map(fragments.map((f) => [f.id, live?.doc(`fragment.${f.id}`)]));
  };

  // The editor, made once per live project.
  useEffect(() => {
    const el = host.current!;
    const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' });
    shadow.adoptedStyleSheets = [editorSheet()];
    const parent = document.createElement('div');
    parent.className = 'ws-host';
    shadow.replaceChildren(parent);
    const fragments = editorFragments(project, live, ids, openers);
    shownKey.current = ids.join('\n');
    piecesShown.current = piecesKey(project, ids, openers);
    const editor = createScrivenings({
      parent,
      fragments,
      readOnly,
      showFormattingMarks: showsMarksNow.current,
      theme: composition ? 'composition' : 'book',
      // A play's scripts are laid out as it prints: US or UK.
      scriptFormat: project.scriptFormat,
      // A musical's songs are shown as songs, with Song on Tab and ⌘7.
      musical: project.musical,
      lang: project.locale || document.documentElement.lang || 'en',
      extensions: [
        annotationExtension((found) => callbacks.current.onAnnotationCaret(found)),
        search({ top: true }),
        keymap.of(searchKeymap),
        ...[...editorExtensionProviders].map((provide) => provide({ project, live })),
      ],
      onEdit(edits: EditorEdit[]) {
        for (const e of edits) {
          const t = mirror.current.get(e.fragmentId) ?? '';
          mirror.current.set(e.fragmentId, t.slice(0, e.from) + e.insert + t.slice(e.to));
        }
        if (!live) return;
        const typed = [...new Set(edits.map((e) => e.fragmentId))];
        // Still in step with the document if it was before: then the edit keeps it so.
        const inStep = new Map(typed.map((id) => [id, synced.current.get(id) === live.doc(`fragment.${id}`)]));
        live.edit((p) => {
          for (const e of edits) p.spliceText(e.fragmentId, e.from, e.to - e.from, e.insert);
        }, { typed });
        for (const id of typed) if (inStep.get(id)) synced.current.set(id, live.doc(`fragment.${id}`));
        callbacks.current.onTyped();
      },
      onCaret: (c) => callbacks.current.onCaret(c),
      onFormat: (f) => callbacks.current.onFormat(f),
      onSelection: (w) => callbacks.current.onSelection?.(w),
      onOpenLink: (address) => callbacks.current.onOpenLink?.(address),
    });
    handle.current = editor;
    syncAll(editor, fragments);
    placeCaret(editor, startAt);
    callbacks.current.onFormat(editor.format());
    // Tests read the whole text (CodeMirror draws only the lines in view), the caret, and place it as the app would.
    Object.defineProperties(el, {
      editorTexts: { configurable: true, get: () => Object.fromEntries(mirror.current) },
      editorCaret: { configurable: true, get: () => editor.caret() },
      editorSelect: {
        configurable: true,
        value: (fragmentId: string, offset: number, to?: number) => {
          editor.select(fragmentId, offset, to ?? offset);
          editor.focus();
        },
      },
      editorSelection: { configurable: true, get: () => editor.selection() },
      editorAnnotation: { configurable: true, value: (id: string) => annotationRange(editor.view.state, id) },
      editorRevisionMarks: { configurable: true, get: () => editor.revisionMarksByFragment() },
    });

    // Another device's change, or this one's made outside the editor: the same splices, at once.
    const follow = (doc: string, beforeHeads: A.Heads | null, after: A.Doc<unknown>) => {
      if (!doc.startsWith('fragment.') || !live) return;
      const id = doc.slice('fragment.'.length);
      const had = mirror.current.get(id);
      if (had === undefined) return;
      const now = live.text(id);
      if (now !== had) for (const s of remoteSplices(had, after, beforeHeads, now)) editor.applyExternal(id, s.from, s.to, s.insert);
      mirror.current.set(id, now);
      synced.current.set(id, live.doc(doc));
    };
    const offRemote = live?.onRemote(follow);
    const offLocal = live?.onLocal(follow);
    // The Revise room rewrites text open here through the editor (undoable), never under it.
    const offOpen = live
      ? registerOpenEditor({
          live,
          fragmentIds: () => [...mirror.current.keys()],
          text: (id) => mirror.current.get(id) ?? null,
          replaceText: (id, text) => mirror.current.has(id) && editor.replaceText(id, text),
          replaceRange: (id, from, to, insert) => {
            if (!mirror.current.has(id) || editor.view.state.readOnly) return false;
            editor.select(id, from, to, { exact: true, scroll: false });
            const r = editor.view.state.selection.main;
            editor.view.dispatch({ changes: { from: r.from, to: r.to, insert }, selection: { anchor: r.from + insert.length }, annotations: Exact.of(true), userEvent: 'input.replace' });
            return true;
          },
        })
      : null;
    return () => {
      offOpen?.();
      offRemote?.();
      offLocal?.();
      editor.destroy();
      handle.current = null;
      void live?.flush();
    };
    // Remade only for another project; the selection and settings follow below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, project.id]);

  // A rebuilt project: another selection, or new titles and openers, redraw the fragments; otherwise only a fragment
  // whose document changed without the editor hearing of it is brought into step.
  useEffect(() => {
    const editor = handle.current;
    if (!editor) return;
    const key = ids.join('\n');
    const pieces = piecesKey(project, ids, openers);
    if (key !== shownKey.current || pieces !== piecesShown.current) {
      const fragments = editorFragments(project, live, ids, openers);
      editor.setFragments(fragments);
      syncAll(editor, fragments);
      piecesShown.current = pieces;
      if (key !== shownKey.current) {
        shownKey.current = key;
        placeCaret(editor, startAt);
        callbacks.current.onCaret(editor.caret());
      }
      return;
    }
    if (!live) return;
    for (const id of ids) {
      const doc = live.doc(`fragment.${id}`);
      if (synced.current.get(id) === doc) continue;
      const now = live.text(id);
      const had = mirror.current.get(id) ?? '';
      const c = minimalChange(had, now);
      if (c) editor.applyExternal(id, c.from, c.to, c.insert);
      mirror.current.set(id, now);
      synced.current.set(id, doc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, ids.join('\n'), openers]);

  useEffect(() => handle.current?.setReadOnly(readOnly), [readOnly]);
  // Settings ▸ Writing ▸ Show formatting marks.
  useEffect(() => handle.current?.setShowFormattingMarks(showsMarks), [showsMarks]);
  const lockedKey = (props.locked ?? []).join('\n');
  useEffect(() => handle.current?.setLockedFragments(props.locked ?? []), [lockedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    handle.current?.setTheme(composition ? 'composition' : 'book');
  }, [composition]);
  useEffect(() => handle.current?.setScriptFormat(project.scriptFormat), [project.scriptFormat]);
  useEffect(() => handle.current?.setMusical(project.musical), [project.musical]);
  // Annotations (after the fragments, so they're placed in the text the editor has): sent again only when they're
  // different (a rebuild after typing usually leaves them as they were, mapped by the editor meanwhile), or when the
  // fragments were redrawn.
  const annotationsSent = useRef('');
  useEffect(() => {
    const key = `${piecesShown.current}\u0000${JSON.stringify(props.annotations)}`;
    if (key === annotationsSent.current) return;
    annotationsSent.current = key;
    handle.current?.view.dispatch({ effects: setAnnotations.of(props.annotations) });
  }, [props.annotations, piecesShown.current]);
  useEffect(() => {
    handle.current?.view.dispatch({ effects: setActiveAnnotation.of(props.activeAnnotation) });
  }, [props.activeAnnotation]);

  return (
    <div
      className="scrivenings"
      ref={host}
      role="group"
      aria-label={label}
      data-fragments={ids.length}
      data-annotations={props.annotations.length}
      onContextMenu={(e) => callbacks.current.onContextMenu(e)}
    />
  );
}

function placeCaret(editor: ScriveningsHandle, at: { fragmentId: string; offset: number } | null) {
  if (at) editor.select(at.fragmentId, at.offset, at.offset, { scroll: true });
}
